<script setup lang="ts">

import NavHub from '../../NavHub'
import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  provide,
  reactive,
  ref,
  shallowReactive,
  shallowReadonly,
  watch,
  watchEffect
} from "vue";
import {contextKey, useContext, ViewContext, ViewNavigateOptions, ViewOptions} from "@hantera/portal-app";
import {v7} from "uuid";
import {PersistedLayer, StackLayer} from "@/view-managers/stack/types";
import ViewFrame from "@/view-managers/stack/ViewFrame.vue";
import {Icon} from "@hantera/design-system";
import LayerListItem from "@/view-managers/stack/LayerListItem.vue";
import ProfileMenu from "@/view-managers/stack/ProfileMenu.vue";
import {defaultViewUriKey, stateKey} from "@/keys";

const animSpanGoHome = 500
const animSpanGoHomeCss = `${animSpanGoHome}ms`
const animSpanLayer = 300
const animSpanLayerCss = `${animSpanLayer}ms`

const context = <ReturnType<typeof useContext>>{
  ...useContext(),
  openView(uri: string, options?: ViewOptions): ViewContext {
    return openLayer(uri, options).viewContext
  },
  getView(viewId: string | undefined): ViewContext | undefined {
    if (!viewId) {
      return
    }
    
    for (const layer of stack) {
      if (layer.viewContext.viewId === viewId) {
        return layer.viewContext
      }
    }
  },
  getChildrenViews(parentViewId?: string): Readonly<ViewContext[]> {
    if (!parentViewId) {
      return []
    }

    const result: ViewContext[] = []
    for (const layer of stack) {
      if (layer.viewContext.parentView === parentViewId) {
        result.push(layer.viewContext)
      }
    }

    return shallowReadonly(result)
  },
}

const visibleLayer = computed(() => {
  if (displayState.value !== 'stack') {
    return
  }
  let max = -1;
  let result: StackLayer | undefined
  for (const layer of stack) {
    if (layer.order > max) {
      max = layer.order
      result = layer
    }
  }
  return result
})

const stack: StackLayer[] = reactive([])

const displayState = ref<'init' | 'home' | 'stack'>('init')
const displayList = ref(false)
const listElem = ref<HTMLDivElement>()

const stateKeyValue = inject(stateKey)
if (stateKeyValue) {
  // Load state from localStorage
  const storageKey = `stack_${stateKeyValue}`
  const persistedStack = JSON.parse(localStorage.getItem(storageKey) || '[]')
  if (Array.isArray(persistedStack)) {
    let order = 0
    for (const layer of persistedStack as PersistedLayer[]) {
      const newLayer = openLayer(layer.path, {
        query: layer.query,
        state: layer.state,
      })
      newLayer.preventAnimation = true
      newLayer.order = order++
    }
  }
  
  // Set up watch to write state back to localStorage
  watch(stack, () => {
    localStorage.setItem(storageKey, JSON.stringify(stack.toSorted((a, b) => a.order - b.order).map(r => (<PersistedLayer>{
      path: r.viewContext.path,
      query: r.viewContext.query,
      state: r.viewContext.state,
    }))));
  })
}

const defaultViewUri = inject(defaultViewUriKey)
if (defaultViewUri && stack.length === 0) {
  const layer = openLayer(defaultViewUri, {})
  layer.preventAnimation = true
}

provide(contextKey, context)

function close(viewId: string) {
  context.getView(viewId)?.close()
}

const stacksOrigin = ref({
  x: 50,
  y: 50
})
const frameElem = ref<HTMLElement>()

onMounted(() => {
  window.addEventListener('pointerdown', setOrigin, {
    capture: true,
    passive: true,
  })
})

onUnmounted(() => {
  window.removeEventListener('pointerdown', setOrigin, {
    capture: true
  })
})

function setOrigin(e: MouseEvent) {

  if (!frameElem.value) {
    stacksOrigin.value = {
      x: 50,
      y: 50
    }
    return
  }
  const x = (e.clientX / frameElem.value.clientWidth) * 100
  const y = (e.clientY / frameElem.value.clientHeight) * 100
  
  stacksOrigin.value = { x, y }
}

function goHome() {
  displayState.value = 'home'
  setTimeout(() => {
    for (const layer of stack) {
      layer.visible = false
    }
  }, animSpanGoHome)
  displayList.value = false;
}

function toggleList() {
  displayList.value = !displayList.value
}

const currentLabel = computed(() => {
  if (displayState.value === 'stack' && visibleLayer.value) {
    return visibleLayer.value.viewContext.name
  }
  return ''
})

function select(layer: StackLayer) {
  layer.visible = true
  layer.order = stack.reduce((max, layer) => {
    if (layer.order > max) {
      return layer.order + 1
    }
    return max + 1
  }, 0)
  displayState.value = 'stack';
  displayList.value = false;
}

function openLayer(uri: string, options?: ViewOptions) {
  const [path, querystring] = uri.split('?')

  const query = options?.query ?? {}
  if (querystring) {
    const params = new URLSearchParams(querystring)
    params.forEach((val, key) => {
      query[key] = val
    })
  }

  const viewPath = ref(path)
  const viewQuery = ref(query)
  const viewState = ref(options?.state ?? {})

  const layer = shallowReactive<StackLayer>({
    order: stack.reduce((max, layer) => {
      if (layer.order > max) {
        return layer.order + 1
      }
      return max + 1
    }, 0),
    visible: true,
    preventAnimation: false,
    viewContext: shallowReactive({
      viewId: v7(),
      name: ref(),
      hasPendingChanges: ref(),
      parentView: options?.parentViewId,
      close() {
        const idx = stack.findIndex(r => r.viewContext.viewId === layer.viewContext.viewId)
        if (idx < 0) {
          // Already closed
          return
        }

        if (stack.length === 1) {
          displayState.value = 'init'
          displayList.value = false
          stack.splice(0, stack.length)
        } else {
          stack.splice(idx, 1)

          if (visibleLayer.value) {
            // Ensure current layer is actually visible
            visibleLayer.value.visible = true
          }
        }
      },
      navigate(uri: string, options?: ViewNavigateOptions) {

        const [path, querystring] = uri.split('?')

        const query = options?.query ?? {}
        if (querystring) {
          const params = new URLSearchParams(querystring)
          params.forEach((val, key) => {
            query[key] = val
          })
        }

        viewPath.value = path
        viewQuery.value = query
        viewState.value = reactive(options?.state ?? {})
      },
    } as ViewContext)
  })

  watchEffect(() => {
    layer.viewContext.path = viewPath.value
    layer.viewContext.query = viewQuery.value
    layer.viewContext.uri = viewPath.value

    if (viewQuery.value) {
      layer.viewContext.uri += '?' + Object.keys(viewQuery.value).map(r => viewQuery.value[r] ? r + '=' + viewQuery.value[r] : r).join('&')
    }

    layer.viewContext.state = viewState.value

    layer.viewContext.visible = displayState.value === 'stack' && visibleLayer.value?.viewContext.viewId === layer.viewContext.viewId
    layer.viewContext.active = layer.viewContext.visible
  })
  stack.push(layer)

  displayState.value = 'stack'
  return layer
}

</script>

<template>
  <div class="stack-view-manager">
    
    <div class="frame" ref="frameElem">

      <NavHub
        class="nav-hub"
      />

      <div
        class="blur"
        :class="{
          'blur--shown': displayState !== 'home' && displayState !== 'init'
        }"
      ></div>
      
      <div
        class="stack"
        :class="{
          'stack--shown': displayState === 'stack',
          'stack--closed': displayState === 'home'
        }"
      >
        <div
          class="layer"
          v-for="layer in stack.toReversed()"
          :key="layer.viewContext.viewId"
          :class="{
            'layer--no-animation': layer.preventAnimation
          }"
          :style="`z-index: ${layer.order}; transform-origin: ${stacksOrigin.x}% ${stacksOrigin.y}%; visibility: ${layer.visible ? null : 'hidden'}`"
        >
          <ViewFrame :layer="layer" class="layer-view" />
          <div
            class="blur"
            :class="{
              'blur--shown': displayState === 'stack' && !layer.viewContext.visible
            }"
          ></div>
        </div>
      </div>
      
      <div
        class="list neat-scroll"
        :class="{
          'list--shown': displayList
        }"
        ref="listElem"
      >
        <TransitionGroup>
            <LayerListItem
              v-if="displayList"
              v-for="layer in stack"
              :key="layer.viewContext.viewId"
              :layer="layer"
              @select="select"
              class="list-item"
            />
        </TransitionGroup>
      </div>
    </div>
    
    <div class="action-bar">
      <button class="btn-reset btn-action btn-home" @click="goHome">
        <Icon name="home" />
      </button>
      <button class="btn-reset btn-action btn-list" @click="toggleList">
        <Icon name="blocks" />
        <span
          class="stack-size"
          :class="{
            'stack-size--shown': stack.length > 0
          }"
        >
          {{stack.length}}
        </span>
      </button>
      <div class="current-label">
        {{currentLabel}}
      </div>
      <ProfileMenu />
    </div>
  </div>
</template>

<style scoped>

.stack-view-manager {
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto max-content;
}

.frame {
  position: relative;
  overflow: hidden;
}

.nav-hub, .stack, .list {
  z-index: 0;
}

.nav-hub, .stack, .layer, .blur, .list {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.blur {
  pointer-events: none;
}
.stack {
  display: none;
}
.stack--shown {
  display: block;
}
.stack--closed {
  pointer-events: none;
  display: block;
  animation: stack-to-home v-bind('animSpanGoHomeCss') forwards;
}

.layer {
  overflow: hidden;
  background-color: hsl(var(--color-100));
}

.layer:not(.layer--no-animation) {
  animation: layer-open forwards 300ms;
}

@keyframes layer-open {
  0% {
    transform: scale(0%);
    opacity: 0;
    border-radius: 100px;
  }
  100% {
    transform: scale(100%);
    opacity: 1;
    border-radius: 0;
  }
}

.blur--shown {
  animation: blur forwards 500ms;
}

@keyframes blur {
  0% {
    background-color: hsla(var(--color-0), 0);
    backdrop-filter: blur(0);
  }
  100% {
    background-color: hsla(var(--color-0), 0.5);
    backdrop-filter: blur(3px);
  }
}

@keyframes stack-to-home {
  0% {
    transform: translateY(0%);
    opacity: 1;
  }
  100% {
    transform: translateY(100%);
    opacity: 0;
  }
}


.list {
  backdrop-filter: blur(5px);
  background-color: hsla(var(--color-0), .3);
  opacity: 0;
  transition: opacity 300ms;
  pointer-events: none;
  
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: max-content;
  padding: 32px 0;
  row-gap: 32px;
  overflow: auto;
}
.list--shown {
  opacity: 1;
  pointer-events: all;
}

.list-item.v-leave-active {
  animation: close-list 300ms;
}

@keyframes close-list {
  0% { opacity: 1; }
  100% { opacity: 0; }
}

.action-bar {
  box-shadow: -10px 1px 1px var(--color-shadow);
  display: grid;
  grid-template-columns: 40px 40px auto 40px;
  height: 40px;
  border-top: 1px solid hsl(var(--color-60));
  align-items: center;
}

.btn-action {
  height: 40px;
}

.btn-action:hover {
  background-color: hsl(var(--color-80));
}

.btn-action :deep(svg) {
  width: 24px;
}

.btn-home {
  border-right: 1px solid hsl(var(--color-60));
}

.btn-list {
  position: relative;
  overflow: visible;
}

.stack-size {
  display: block;
  position: absolute;
  top: 5px;
  right: 0;
  background-color: hsl(var(--color-primary));
  height: 14px;
  font-size: 12px;
  color: white;
  padding: 0 4px;
  border-radius: 7px;
  opacity: 0;
  transition: opacity 200ms;
}

.stack-size--shown {
  opacity: 1;
}

.current-label {
  line-height: 40px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
}

</style>